/**
 * Job polling — plan D5, ARCHITECTURE §04.5 and §10.4.
 *
 * **Polls on `terminal`, never on a status list.** `ClientJob` carries both our
 * seven-value `status` and a `terminal` boolean precisely so the client does
 * not have to know the enum: an eighth status added next year stops the poll
 * correctly here, whereas `['SUCCEEDED','FAILED'].includes(status)` would spin
 * forever on it.
 *
 * The presentation rules live in `describeJob` below rather than in a component,
 * because the same judgement is needed in the drawer, in a toast and in the
 * dashboard, and getting `EXPIRED` wrong in any one of them is the failure the
 * architecture calls out by name.
 */

import type { ClientJob } from '@rd/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { apiRequest, ApiClientError } from './apiClient.js';

export const JOB_POLL_INTERVAL_MS = 5_000;

export interface UseJobResult {
  job: ClientJob | undefined;
  isPolling: boolean;
  /** `POST /jobs/{ref}/refresh` — the EXPIRED escape hatch. */
  refresh(): Promise<void>;
  isRefreshing: boolean;
  error: ApiClientError | null;
}

export function jobQueryKey(ref: string): readonly unknown[] {
  return ['job', ref];
}

export function useJob(ref: string | null): UseJobResult {
  const queryClient = useQueryClient();

  const query = useQuery<ClientJob>({
    queryKey: jobQueryKey(ref ?? ''),
    queryFn: () => apiRequest<ClientJob>(`/jobs/${ref ?? ''}`),
    enabled: ref !== null,
    // Zero: a job's state is never "fresh enough" — the whole point is that it
    // is changing underneath us.
    staleTime: 0,
    refetchInterval: (q) => {
      const job = q.state.data;
      // `undefined` before the first response: keep polling, we do not yet know.
      if (job && job.terminal) return false;
      return JOB_POLL_INTERVAL_MS;
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => apiRequest<ClientJob>(`/jobs/${ref ?? ''}/refresh`, { method: 'POST' }),
    onSuccess: (fresh) => {
      // Seed the cache directly rather than invalidating: the response *is* the
      // new state, and an invalidate would fire a second request for it.
      queryClient.setQueryData(jobQueryKey(ref ?? ''), fresh);
    },
  });

  const refresh = useCallback(async () => {
    await refreshMutation.mutateAsync();
  }, [refreshMutation]);

  return {
    job: query.data,
    isPolling: query.data !== undefined && !query.data.terminal,
    refresh,
    isRefreshing: refreshMutation.isPending,
    error: query.error instanceof ApiClientError ? query.error : null,
  };
}

/* ------------------------------------------------------------------ *
 * §04.5 — how a job reads. One table, one place.
 * ------------------------------------------------------------------ */

export type JobTone = 'progress' | 'success' | 'warning' | 'error' | 'neutral';

export interface JobPresentation {
  tone: JobTone;
  headline: string;
  detail: string;
  /** When set, the UI must offer a control bound to `refresh()`. */
  recheck: boolean;
}

/**
 * **`EXPIRED` is neutral, never an error.**
 *
 * §10.4: it means *we stopped asking after 24 hours*, not *it did not happen*.
 * The operation may well have completed upstream. Copy that says "failed" is
 * how an operator re-submits an activation that is still running — a real
 * duplicate on a real SIM. Criterion 19 asserts the rendered text contains none
 * of "failed", "error" or "unsuccessful", and does offer a re-check.
 *
 * The same applies to a `SUBMITTED` job carrying `VENDOR_JOB_ID_COLLISION`
 * (Phase 03 defect D3-02): the network accepted the work and we are still
 * matching it up, so it renders as in progress.
 */
export function describeJob(job: ClientJob, errorCode?: string | null): JobPresentation {
  const { counts } = job;

  if (errorCode === 'VENDOR_JOB_ID_COLLISION' && !job.terminal) {
    return {
      tone: 'progress',
      headline: 'Confirming with the network…',
      detail: 'The network accepted this. We are matching it to your request.',
      recheck: false,
    };
  }

  switch (job.status) {
    case 'QUEUED':
    case 'SUBMITTED':
    case 'RUNNING':
      return {
        tone: 'progress',
        headline: 'In progress',
        detail:
          counts.total > 0
            ? `${counts.succeeded + counts.failed + counts.rejected} of ${counts.total} done`
            : 'Sending to the network…',
        recheck: false,
      };

    case 'SUCCEEDED':
      return {
        tone: 'success',
        headline: 'Completed',
        detail: counts.total > 0 ? `All ${counts.total} completed successfully.` : 'Completed.',
        recheck: false,
      };

    case 'PARTIAL':
      // Warning, not error: most of it worked, and calling it an error would
      // hide the part that did.
      return {
        tone: 'warning',
        headline: 'Partly completed',
        detail: `${counts.succeeded} of ${counts.total} succeeded. ${
          counts.total - counts.succeeded
        } need attention.`,
        recheck: false,
      };

    case 'FAILED':
      return {
        tone: 'error',
        headline: 'This did not run',
        detail: 'Nothing was changed. Open it to see the reason for each item.',
        recheck: false,
      };

    case 'EXPIRED':
      return {
        tone: 'neutral',
        headline: 'We stopped tracking this',
        detail:
          'We stopped tracking this after 24 hours. It may still have completed upstream. Re-check to ask the network for its current state.',
        recheck: true,
      };

    default: {
      // A status this build has never heard of. Neutral and honest beats a
      // confident wrong label — and `terminal` already stopped the poll.
      return {
        tone: 'neutral',
        headline: 'Status unavailable',
        detail: 'We cannot describe the state of this right now.',
        recheck: true,
      };
    }
  }
}
